import { filterClasses } from "@swivl-mono/helpers";
import { FacilityAndSpaceFeatures } from "@swivl-mono/types";
import React, { useState } from "react";

export type UnitSizeBucket = "small" | "medium" | "large" | "xl";

export type UnitImageKind =
  | UnitSizeBucket
  | "vehicle-parking-uncovered"
  | "vehicle-parking-covered"
  | "vehicle-parking-enclosed"
  | "rv-uncovered"
  | "rv-covered"
  | "rv-enclosed"
  | "boat-uncovered"
  | "boat-covered"
  | "trailer"
  | "locker"
  | "wine"
  | "warehouse"
  | "office"
  | "portable";

/**
 * CDN-hosted illustrations keyed by `UnitImageKind`. Kinds intentionally
 * absent fall through `UNIT_KIND_FALLBACK` until an entry exists; see the
 * spec sheet at `/tmp/unit-image-kinds.csv` (or wherever the operator
 * filled in URLs) for the canonical assignment.
 */
const UNIT_KIND_IMAGE_URLS: Partial<Record<UnitImageKind, string>> = {
  small: "https://swivl-cdn.s3-us-west-1.amazonaws.com/unit-small%402x.png",
  medium: "https://swivl-cdn.s3-us-west-1.amazonaws.com/unit-medium%402x.png",
  large: "https://swivl-cdn.s3-us-west-1.amazonaws.com/unit-large%402x.png",
  xl: "https://swivl-cdn.s3-us-west-1.amazonaws.com/unit-extra-large%402x.png",
  "vehicle-parking-uncovered": "https://swivl-cdn.s3-us-west-1.amazonaws.com/unit-parking-uncovered-car%402x.png",
  "vehicle-parking-covered": "https://swivl-cdn.s3-us-west-1.amazonaws.com/unit-parking-covered-car%402x.png",
  "vehicle-parking-enclosed": "https://swivl-cdn.s3-us-west-1.amazonaws.com/unit-parking-enclosed-car%402x.png",
  "rv-uncovered": "https://swivl-cdn.s3-us-west-1.amazonaws.com/unit-parking-uncovered-rv%402x.png",
  "rv-covered": "https://swivl-cdn.s3-us-west-1.amazonaws.com/unit-parking-covered-rv%402x.png",
  "rv-enclosed": "https://swivl-cdn.s3-us-west-1.amazonaws.com/unit-parking-enclosed-rv%402x.png",
  "boat-uncovered": "https://swivl-cdn.s3-us-west-1.amazonaws.com/unit-parking-uncovered-boat%402x.png",
  "boat-covered": "https://swivl-cdn.s3-us-west-1.amazonaws.com/unit-parking-covered-boat%402x.png",
  trailer: "https://swivl-cdn.s3-us-west-1.amazonaws.com/unit-parking-uncovered-boat%402x.png",
  wine: "https://swivl-cdn.s3-us-west-1.amazonaws.com/unit-wine%402x.png",
  warehouse: "https://swivl-cdn.s3-us-west-1.amazonaws.com/unit-commercial-warehouse%402x.png",
  office: "https://swivl-cdn.s3-us-west-1.amazonaws.com/unit-office-space%402x.png"
  // `locker` and `portable` intentionally absent — see UNIT_KIND_FALLBACK.
};

/**
 * When a kind has no URL in `UNIT_KIND_IMAGE_URLS`, the resolver walks
 * this chain until it finds one. `null` marks the chain root (no further
 * fallback). `medium` is the root storage fallback.
 */
const UNIT_KIND_FALLBACK: Record<UnitImageKind, UnitImageKind | null> = {
  small: "medium",
  medium: null,
  large: "medium",
  xl: "large",
  "vehicle-parking-uncovered": "medium",
  "vehicle-parking-covered": "vehicle-parking-uncovered",
  "vehicle-parking-enclosed": "vehicle-parking-covered",
  "rv-uncovered": "medium",
  "rv-covered": "rv-uncovered",
  "rv-enclosed": "rv-covered",
  "boat-uncovered": "medium",
  "boat-covered": "boat-uncovered",
  trailer: "medium",
  locker: "small",
  wine: "medium",
  warehouse: "xl",
  office: "medium",
  portable: "medium"
};

function resolveImageUrl(kind: UnitImageKind): string | undefined {
  let current: UnitImageKind | null = kind;
  // Bounded by the size of the union — even worst-case is < 20 hops, so a
  // plain while-walk over the fallback chain is fine.
  while (current) {
    const url = UNIT_KIND_IMAGE_URLS[current];
    if (url) return url;
    current = UNIT_KIND_FALLBACK[current];
  }
  return undefined;
}

interface UnitThumbnailProps {
  /** Optional unit-specific thumbnail URL. Takes precedence over `kind`. */
  imageUrl?: string;
  /**
   * Resolved illustration kind. Compute with `unitImageKindFor` (preferred)
   * or `sizeBucketFromDimensions` / `sizeBucketFromText`. Defaults to
   * "medium" when unspecified.
   */
  kind?: UnitImageKind;
  /** Alt text — typically the unit size label (e.g. "5x5"). */
  alt?: string;
  /** Extra Tailwind on the outer tile. Default size: 72×60. */
  className?: string;
}

/**
 * 72×60 thumbnail tile used by Step 1 unit cards + Step 2/3 unit-summary
 * cards. Source precedence: per-unit `imageUrl` → kind CDN illustration
 * → inline SVG (only if the CDN image errors). Per Figma 276:7687 /
 * 198:11030.
 */
export function UnitThumbnail({ imageUrl, kind = "medium", alt = "", className }: UnitThumbnailProps): React.ReactElement {
  const [imgFailed, setImgFailed] = useState(false);
  const effectiveSrc = imageUrl ?? resolveImageUrl(kind);

  return (
    <div
      className={filterClasses(
        "flex h-[60px] w-[72px] shrink-0 items-center justify-center overflow-hidden rounded bg-grey-50 text-grey-500",
        className
      )}
    >
      {imgFailed || !effectiveSrc ? (
        <UnitSizeIllustration bucket={sizeBucketFromKind(kind)} />
      ) : (
        <img src={effectiveSrc} alt={alt} loading="lazy" onError={() => setImgFailed(true)} className="max-h-full max-w-full object-contain" />
      )}
    </div>
  );
}

/**
 * Pick the right illustration kind for a unit. Checks FMS-derived features
 * in priority order (most specific → most generic), then falls back to the
 * size bucket for plain storage units. Pass a `UnitOption`-shaped object.
 */
export function unitImageKindFor(unit: {
  width?: number;
  length?: number;
  dimensions?: string;
  features?: readonly FacilityAndSpaceFeatures[];
}): UnitImageKind {
  const features = new Set(unit.features ?? []);

  // RV — checked first so a "RV + covered" unit picks the RV image, not vehicle.
  if (features.has(FacilityAndSpaceFeatures.EnclosedRVParking)) return "rv-enclosed";
  if (features.has(FacilityAndSpaceFeatures.CoveredRVParking)) return "rv-covered";
  if (features.has(FacilityAndSpaceFeatures.RVParking)) return "rv-uncovered";

  // Boat
  if (features.has(FacilityAndSpaceFeatures.BoatParking)) return "boat-uncovered";

  // Vehicle / car parking — covered/enclosed take priority over generic.
  if (features.has(FacilityAndSpaceFeatures.IndoorVehicleStorage)) return "vehicle-parking-enclosed";
  if (features.has(FacilityAndSpaceFeatures.CoveredVehicleStorage)) return "vehicle-parking-covered";
  if (features.has(FacilityAndSpaceFeatures.Parking) || features.has(FacilityAndSpaceFeatures.SmallVehicleStorage)) {
    return "vehicle-parking-uncovered";
  }

  // Specialty
  if (features.has(FacilityAndSpaceFeatures.TrailerStorage)) return "trailer";
  if (features.has(FacilityAndSpaceFeatures.Locker)) return "locker";
  if (features.has(FacilityAndSpaceFeatures.WineStorage)) return "wine";
  if (features.has(FacilityAndSpaceFeatures.CommercialWarehouse)) return "warehouse";
  if (features.has(FacilityAndSpaceFeatures.OfficeSpace)) return "office";
  if (features.has(FacilityAndSpaceFeatures.ContainerUnit)) return "portable";

  // Plain storage — fall back to size bucket.
  if (unit.width && unit.length) return sizeBucketFromDimensions(unit.width, unit.length);
  return sizeBucketFromText(unit.dimensions);
}

/**
 * Derive a size bucket from raw feet dimensions. Buckets follow the
 * `UnitFilters.sizeBucket` cutoffs used elsewhere in the picker.
 */
export function sizeBucketFromDimensions(width?: number, length?: number): UnitSizeBucket {
  if (!width || !length) return "medium";
  const sqft = width * length;
  if (sqft <= 50) return "small"; // 5×5, 5×10
  if (sqft <= 100) return "medium"; // 10×10
  if (sqft <= 200) return "large"; // 10×15, 10×20
  return "xl"; // 10×25+
}

/**
 * Derive a size bucket from a human-readable dimensions/size string
 * (e.g. "5x5", "10 × 10", "10x20"). Falls back to "medium" when the
 * text can't be parsed.
 */
export function sizeBucketFromText(text?: string): UnitSizeBucket {
  if (!text) return "medium";
  const match = text.replace(/\s/g, "").match(/(\d+)[x×](\d+)/i);
  if (!match) return "medium";
  return sizeBucketFromDimensions(Number(match[1]), Number(match[2]));
}

function sizeBucketFromKind(kind: UnitImageKind): UnitSizeBucket {
  if (kind === "small" || kind === "medium" || kind === "large" || kind === "xl") return kind;
  // Everything else collapses to "medium" for the SVG last-resort fallback.
  return "medium";
}

function UnitSizeIllustration({ bucket }: { bucket: UnitSizeBucket }): React.ReactElement {
  // Last-resort fallback when the CDN image errors. Same 72×60 viewport
  // with a roll-up door sized to the bucket.
  const widthByBucket: Record<UnitSizeBucket, number> = { small: 18, medium: 28, large: 40, xl: 54 };
  const w = widthByBucket[bucket];
  const x = (72 - w) / 2;
  return (
    <svg
      width="72"
      height="60"
      viewBox="0 0 72 60"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <line x1="4" y1="50" x2="68" y2="50" />
      <rect x={x} y={14} width={w} height={36} />
      <line x1={x} y1={22} x2={x + w} y2={22} />
      <line x1={x} y1={30} x2={x + w} y2={30} />
      <line x1={x} y1={38} x2={x + w} y2={38} />
      <line x1={x} y1={46} x2={x + w} y2={46} />
    </svg>
  );
}
